import axios, { AxiosInstance, AxiosError } from 'axios';
import {
    ClientConfig,
    ApiResponse,
    SubmitCaptchaRequest,
    TaskResponse,
    TaskResult,
    UserBalance,
    UserTask,
    PaginatedResponse,
    CaptchaSolverError,
    ValidationError,
    AuthenticationError,
    InsufficientBalanceError,
    RateLimitError,
    TimeoutError,
} from './types';

/**
 * Main CaptchaSolver Client Class
 *
 * @example
 * ```typescript
 * import { CaptchaSolverClient } from 'captcha-solver-client';
 *
 * const client = new CaptchaSolverClient({
 *   apiKey: 'your-api-key',
 *   baseURL: 'https://your-api.com/api'
 * });
 *
 * // Solve a Turnstile captcha
 * const solution = await client.solveCaptcha({
 *   type: 'turnstile',
 *   sitekey: '0x4AAAAAAAa0Ic88byebJ1dj',
 *   pageurl: 'https://example.com'
 * });
 * ```
 */
export class CaptchaSolverClient {
    private client: AxiosInstance;
    private config: Required<ClientConfig>;

    constructor(config: ClientConfig) {
        this.config = {
            baseURL: 'http://localhost:3000/api',
            timeout: 30000,
            retries: 3,
            retryDelay: 1000,
            ...config,
        };

        this.client = axios.create({
            baseURL: this.config.baseURL,
            timeout: this.config.timeout,
            headers: {
                'X-API-Key': this.config.apiKey,
                'Content-Type': 'application/json',
                'User-Agent': 'captcha-solver-client/1.0.0',
            },
        });

        // Add response interceptor for error handling
        this.client.interceptors.response.use(
            response => response,
            (error: AxiosError) => {
                throw this.handleError(error);
            }
        );
    }

    /**
     * Submit a captcha for solving
     */
    async submitCaptcha(data: SubmitCaptchaRequest): Promise<TaskResponse> {
        try {
            const response = await this.client.post<ApiResponse<TaskResponse>>(
                '/captcha/submit',
                data
            );

            if (response.data.status === 'error') {
                throw new CaptchaSolverError(
                    response.data.error || 'Unknown error',
                    response.data.errorCode
                );
            }

            return response.data.data!;
        } catch (error) {
            if (error instanceof CaptchaSolverError) {
                throw error;
            }
            throw new CaptchaSolverError('Failed to submit captcha');
        }
    }

    /**
     * Get task result
     */
    async getTaskResult(taskId: string): Promise<TaskResult> {
        try {
            const response = await this.client.get<ApiResponse<TaskResult>>(
                `/captcha/result?id=${taskId}`
            );

            if (response.data.status === 'error') {
                throw new CaptchaSolverError(
                    response.data.error || 'Unknown error',
                    response.data.errorCode
                );
            }

            const result = response.data.data!;

            // Handle both 'solution' and 'result' field names for backward compatibility
            if (!result.solution && (result as any).result) {
                result.solution = (result as any).result;
            }

            return result;
        } catch (error) {
            if (error instanceof CaptchaSolverError) {
                throw error;
            }
            throw new CaptchaSolverError('Failed to get task result');
        }
    }

    /**
     * Wait for task completion with polling
     */
    async waitForResult(
        taskId: string,
        options: {
            maxAttempts?: number;
            pollInterval?: number;
            onProgress?: (attempt: number, status: string) => void;
        } = {}
    ): Promise<TaskResult> {
        const { maxAttempts = 60, pollInterval = 5000, onProgress } = options;

        let attempts = 0;

        while (attempts < maxAttempts) {
            attempts++;

            try {
                const result = await this.getTaskResult(taskId);

                if (onProgress) {
                    onProgress(attempts, result.status);
                }

                if (result.status === 'completed') {
                    return result;
                }

                if (result.status === 'failed') {
                    throw new CaptchaSolverError(result.error || 'Task failed');
                }

                // Wait before next poll
                await this.delay(pollInterval);
            } catch (error) {
                if (error instanceof CaptchaSolverError) {
                    throw error;
                }

                // If it's the last attempt, throw the error
                if (attempts >= maxAttempts) {
                    throw new TimeoutError('Task polling timeout');
                }

                // Otherwise, wait and retry
                await this.delay(pollInterval);
            }
        }

        throw new TimeoutError('Task polling timeout');
    }

    /**
     * Solve captcha and wait for result (convenience method)
     */
    async solveCaptcha(
        data: SubmitCaptchaRequest,
        options?: {
            maxAttempts?: number;
            pollInterval?: number;
            onProgress?: (attempt: number, status: string) => void;
        }
    ): Promise<string> {
        const task = await this.submitCaptcha(data);
        const result = await this.waitForResult(task.taskId, options);

        if (!result.solution) {
            throw new CaptchaSolverError('No solution returned');
        }

        return result.solution;
    }

    /**
     * Get user balance
     */
    async getBalance(): Promise<UserBalance> {
        try {
            const response = await this.client.get<ApiResponse<UserBalance>>('/user/balance');

            if (response.data.status === 'error') {
                throw new CaptchaSolverError(
                    response.data.error || 'Unknown error',
                    response.data.errorCode
                );
            }

            return response.data.data!;
        } catch (error) {
            if (error instanceof CaptchaSolverError) {
                throw error;
            }
            throw new CaptchaSolverError('Failed to get balance');
        }
    }

    /**
     * Get user tasks
     */
    async getTasks(
        options: {
            page?: number;
            limit?: number;
            status?: string;
        } = {}
    ): Promise<PaginatedResponse<UserTask>> {
        try {
            const params = new URLSearchParams();
            if (options.page) params.append('page', options.page.toString());
            if (options.limit) params.append('limit', options.limit.toString());
            if (options.status) params.append('status', options.status);

            const response = await this.client.get<ApiResponse<PaginatedResponse<UserTask>>>(
                `/user/tasks?${params.toString()}`
            );

            if (response.data.status === 'error') {
                throw new CaptchaSolverError(
                    response.data.error || 'Unknown error',
                    response.data.errorCode
                );
            }

            const data = response.data.data!;

            // Handle both paginated and simple array responses
            if (Array.isArray(data)) {
                return {
                    items: data,
                    pagination: {
                        page: 1,
                        limit: data.length,
                        total: data.length,
                        pages: 1,
                    },
                };
            }

            return data;
        } catch (error) {
            if (error instanceof CaptchaSolverError) {
                throw error;
            }
            throw new CaptchaSolverError('Failed to get tasks');
        }
    }

    /**
     * Report incorrect solution
     */
    async reportIncorrect(taskId: string): Promise<void> {
        try {
            const response = await this.client.post<ApiResponse>('/captcha/report', { taskId });

            if (response.data.status === 'error') {
                throw new CaptchaSolverError(
                    response.data.error || 'Unknown error',
                    response.data.errorCode
                );
            }
        } catch (error) {
            if (error instanceof CaptchaSolverError) {
                throw error;
            }
            throw new CaptchaSolverError('Failed to report incorrect solution');
        }
    }

    /**
     * Update API key
     */
    setApiKey(apiKey: string): void {
        this.config.apiKey = apiKey;
        this.client.defaults.headers['X-API-Key'] = apiKey;
    }

    /**
     * Update base URL
     */
    setBaseURL(baseURL: string): void {
        this.config.baseURL = baseURL;
        this.client.defaults.baseURL = baseURL;
    }

    /**
     * Handle axios errors and convert to custom errors
     */
    private handleError(error: AxiosError): CaptchaSolverError {
        if (error.response) {
            const { status, data } = error.response;
            const errorData = data as ApiResponse;
            const message = errorData.error || error.message;
            const code = errorData.errorCode;

            switch (status) {
                case 400:
                    return new ValidationError(message);
                case 401:
                    return new AuthenticationError(message);
                case 402:
                    return new InsufficientBalanceError(message);
                case 429:
                    return new RateLimitError(message);
                case 408:
                    return new TimeoutError(message);
                default:
                    return new CaptchaSolverError(message, code, status);
            }
        }

        if (error.code === 'ECONNABORTED') {
            return new TimeoutError('Request timeout');
        }

        return new CaptchaSolverError(error.message || 'Network error');
    }

    /**
     * Utility method for delays
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
